import { EOIcon } from '@nx/ui/eo';
import { useI18n } from '@nx/i18n';
import { countDaysInclusive } from '@nx/domain';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { eventCreateRequested } from '../../store/slices/eventsSlice';

export function EventFormPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const saving = useAppSelector((s) => s.events.saving);
  const created = useAppSelector((s) => s.events.current);
  const submitted = useRef(false);

  const [title, setTitle] = useState('');
  const [venue, setVenue] = useState('');
  const [desc, setDesc] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [maxN, setMaxN] = useState('60');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [touched, setTouched] = useState(false);

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!title.trim()) e.title = t('eo.form.nameRequired');
    if (!venue.trim()) e.venue = t('eo.form.venueRequired');
    if (!start) e.start = t('eo.form.startRequired');
    if (!end) e.end = t('eo.form.endRequired');
    else if (start && end < start) e.end = t('eo.form.endBeforeStart');
    return e;
  }, [title, venue, start, end, t]);

  const days = countDaysInclusive(start, end);
  const valid = Object.keys(errors).length === 0;

  useEffect(() => {
    if (submitted.current && !saving && created?.id) {
      navigate(`/eo/events/${created.id}/agenda`);
    }
  }, [saving, created, navigate]);

  function submit() {
    setTouched(true);
    if (!valid) return;
    submitted.current = true;
    dispatch(
      eventCreateRequested({
        input: {
          title,
          location: venue,
          description: desc,
          startDate: new Date(start),
          endDate: new Date(end),
          maxParticipants: Number(maxN) || null,
        },
        coverFile,
      }),
    );
  }

  return (
    <>
      <p className="eo-bar-crumb">
        <button type="button" className="subtle-link" onClick={() => navigate('/eo/events')}>
          {t('eo.nav.events')}
        </button>
        <span className="sep">›</span>
        {t('eo.nav.newEvent')}
      </p>
      <div className="page-head">
        <div>
          <h2>{t('eo.nav.newEvent')}</h2>
          <p>{t('eo.form.newSub')}</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 24 }}>
        <div className="card card-pad-lg">
          <div style={{ display: 'grid', gap: 16 }}>
            <div className={`field ${touched && errors.title ? 'error' : ''}`}>
              <label>{t('eo.form.name')} *</label>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
              {touched && errors.title ? <span className="err">{errors.title}</span> : null}
            </div>
            <div className={`field ${touched && errors.venue ? 'error' : ''}`}>
              <label>{t('eo.form.venue')} *</label>
              <input className="input" value={venue} onChange={(e) => setVenue(e.target.value)} />
              {touched && errors.venue ? <span className="err">{errors.venue}</span> : null}
            </div>
            <div className="field">
              <label>{t('eo.form.description')}</label>
              <textarea className="textarea" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div className={`field ${touched && errors.start ? 'error' : ''}`}>
                <label>{t('eo.form.start')} *</label>
                <input type="date" className="input" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className={`field ${touched && errors.end ? 'error' : ''}`}>
                <label>{t('eo.form.end')} *</label>
                <input type="date" className="input" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
              <div className="field">
                <label>{t('eo.form.totalDays')}</label>
                <div className="input" style={{ display: 'flex', alignItems: 'center', background: 'var(--background-soft)' }}>
                  {days ? `${days} ${days === 1 ? t('eo.form.day') : t('eo.form.days')}` : '—'}
                </div>
              </div>
            </div>
            <div className="field">
              <label>{t('eo.form.cap')}</label>
              <input className="input" type="number" min={1} value={maxN} onChange={(e) => setMaxN(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="card card-pad-lg">
          <p className="eyebrow">{t('eo.form.cover')}</p>
          <div
            style={{
              aspectRatio: '16/10',
              background: 'var(--background-soft)',
              border: '1px dashed var(--border-strong)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--muted-foreground)',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <EOIcon n="img" size={28} />
            <span style={{ fontSize: 12 }}>{t('eo.form.coverHint')}</span>
            <label className="btn btn-outline btn-sm" style={{ marginTop: 6 }}>
              <EOIcon n="upload" size={14} />
              {t('eo.form.chooseFile')}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => setCoverFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button type="button" className="btn btn-ghost" onClick={() => navigate('/eo/events')}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={submit}>
          {saving ? t('eo.common.saving') : t('eo.form.saveContinue')}
          {!saving ? <EOIcon n="arrR" size={16} /> : null}
        </button>
      </div>
    </>
  );
}
